import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations from '../data/translations';

const LanguageContext = createContext();

export const LANGUAGES = [
  { code: 'ru', label: 'RU', name: 'Русский' },
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'lv', label: 'LV', name: 'Latviešu' },
  { code: 'lt', label: 'LT', name: 'Lietuvių' },
  { code: 'pl', label: 'PL', name: 'Polski' },
];

// Map country codes to our supported languages
const countryToLang = {
  RU: 'ru', BY: 'ru', KZ: 'ru', UA: 'ru', MD: 'ru', KG: 'ru', UZ: 'ru', TJ: 'ru', AM: 'ru', AZ: 'ru', GE: 'ru',
  LV: 'lv',
  LT: 'lt',
  PL: 'pl',
  EE: 'ru', // Estonia - many Russian speakers use Lux Express
  FI: 'en',
  GB: 'en', US: 'en', IE: 'en', AU: 'en', NZ: 'en', CA: 'en',
  DE: 'en', FR: 'en', IT: 'en', ES: 'en', NL: 'en', SE: 'en', NO: 'en', DK: 'en',
};

const getStoredLang = () => {
  try {
    return localStorage.getItem('lux-lang');
  } catch {
    return null;
  }
};

const storeLang = (lang) => {
  try {
    localStorage.setItem('lux-lang', lang);
  } catch {}
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => getStoredLang() || 'en');
  const [detected, setDetected] = useState(false);

  // Detect language by IP on first visit
  useEffect(() => {
    const stored = getStoredLang();
    if (stored) {
      setDetected(true);
      return;
    }

    // Try to detect country by IP
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.country_code) {
          const detectedLang = countryToLang[data.country_code] || 'en';
          setLangState(detectedLang);
          storeLang(detectedLang);
        }
      })
      .catch(() => {
        // Fallback: try browser language
        const browserLang = navigator.language?.slice(0, 2);
        const supported = ['ru', 'en', 'lv', 'lt', 'pl'];
        if (supported.includes(browserLang)) {
          setLangState(browserLang);
          storeLang(browserLang);
        }
      })
      .finally(() => setDetected(true));
  }, []);

  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    storeLang(newLang);
  }, []);

  const t = useCallback(
    (key, params) => {
      const entry = translations[key];
      if (!entry) return key;
      let text = entry[lang] || entry['en'] || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(`{${k}}`, v);
        });
      }
      return text;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, detected }}>
      {children}
    </LanguageContext.Provider>
  );
};
